import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import propertyCheckService from '../services/propertyCheckService';

const PropertyCheckPaymentSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [latestCheck, setLatestCheck] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmError, setConfirmError] = useState(null);
  const [confirmed, setConfirmed] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        if (sessionId) {
          const result = await propertyCheckService.confirmStripeCheckout(sessionId);
          if (result.propertyCheck) {
            setLatestCheck(result.propertyCheck);
            setConfirmed(result.confirmation?.status === 'paid');
            return;
          }
        }
        const checks = await propertyCheckService.listMine();
        setLatestCheck(checks[0] || null);
      } catch (err) {
        setConfirmError(err?.response?.data?.error || err.message || 'Could not confirm payment yet.');
        try {
          const checks = await propertyCheckService.listMine();
          setLatestCheck(checks[0] || null);
        } catch (_) {
          setLatestCheck(null);
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [sessionId]);

  useEffect(() => {
    if (!loading && latestCheck?.id && confirmed) {
      const timeout = setTimeout(() => {
        navigate(`/property-check/c/${latestCheck.id}`, { replace: true });
      }, 1200);
      return () => clearTimeout(timeout);
    }
    return undefined;
  }, [confirmed, latestCheck?.id, loading, navigate]);

  useEffect(() => {
    if (!loading && !latestCheck) {
      (async () => {
        try {
          const checks = await propertyCheckService.listMine();
          setLatestCheck(checks[0] || null);
        } catch (_) {
          // Leave the existing informational state in place.
        }
      })();
    }
  }, [latestCheck, loading]);

  return (
    <Container maxWidth="sm" sx={{ py: { xs: 5, md: 9 } }}>
      <Paper elevation={1} sx={{ p: { xs: 3, md: 5 }, borderRadius: 3, textAlign: 'center' }}>
        <CheckCircleIcon color="success" sx={{ fontSize: 58, mb: 2 }} />
        <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
          {confirmed ? 'Payment confirmed' : 'Payment received'}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          {confirmed
            ? 'Your booking is confirmed. We are matching it with an available verifier now.'
            : 'We are confirming your booking and matching it with an available verifier.'}
        </Typography>

        {loading && (
          <Box sx={{ py: 2 }}>
            <CircularProgress size={28} />
          </Box>
        )}

        {!loading && confirmError && (
          <Alert severity="warning" sx={{ mb: 3, textAlign: 'left' }}>
            {confirmError}
          </Alert>
        )}

        {!loading && !latestCheck && (
          <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
            Your payment was accepted. If your booking does not appear immediately, refresh the
            dashboard in a moment while Stripe finishes confirmation.
          </Alert>
        )}

        {!loading && latestCheck?.payment?.status === 'pending_payment' && (
          <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
            Stripe accepted the payment, but Eqaya is still waiting for final confirmation.
            This usually clears in a moment.
          </Alert>
        )}

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="center">
          {latestCheck && (
            <Button
              variant="contained"
              onClick={() => navigate(`/property-check/c/${latestCheck.id}`)}
            >
              View this check
            </Button>
          )}
          <Button variant={latestCheck ? 'outlined' : 'contained'} onClick={() => navigate('/property-check/dashboard')}>
            Go to dashboard
          </Button>
        </Stack>
      </Paper>
    </Container>
  );
};

export default PropertyCheckPaymentSuccess;
